import { Box, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';
import { match } from 'ts-pattern';

import { useDeleteFiatAccountMutation } from '@/api/v1';
import TrashIcon from '@/assets/icons/trash.svg?react';
import { ConfirmationModal } from '@/components/molecules/ConfirmationModal';
import { ContactDetailsSection } from '@/components/molecules/ContactDetailsSection';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import { ContactDetailsSectionDisplayableEntry } from '@/types/contactDetailsSection';
import {
  AccountDetails,
  ACHAccountDetails,
  COPAccountDetails,
  CVUAccountDetails,
  FiatAccount,
  PIXAccountDetails,
  SpeiAccountDetails,
  SwiftAccountDetails,
  WireAccountDetails,
} from '@/types/contacts';
import {
  isACHAccountDetails,
  isCOPAccountDetails,
  isCVUAccountDetails,
  isPIXAccountDetails,
  isSpeiAccountDetails,
  isSwiftAccountDetails,
  isWireAccountDetails,
} from '@/types/contacts.ext';

interface Props {
  readonly paymentMethod: FiatAccount;
}

export const FiatPaymentMethodCard: React.FC<Props> = (props: Props): React.ReactElement => {
  const { paymentMethod } = props;

  const tr = useTranslator();
  const toast = useToast();

  const [deleteFiatAccount, deleteFiatAccountMutation] = useDeleteFiatAccountMutation();
  const [deleteConfirm, setDeleteConfirm] = React.useState<boolean>(false);

  React.useEffect((): void => {
    switch (deleteFiatAccountMutation.status) {
      case QueryStatus.rejected:
        toast.show(tr('AN_ERROR_OCCURRED'), 'error');
        break;
      case QueryStatus.fulfilled:
        toast.show(tr('DELETE_PAYMENT_METHOD_SUCCESS'), 'success');
        break;
      default:
        // Ignore
        break;
    }
  }, [deleteFiatAccountMutation.status, toast, tr]);

  const onDeleteConfirm = React.useCallback((): void => {
    setDeleteConfirm(true);
  }, []);

  const onDeleteConfirmed = React.useCallback((): void => {
    deleteFiatAccount(paymentMethod.id);
  }, [deleteFiatAccount, paymentMethod.id]);

  const onDeleteAborted = React.useCallback((): void => {
    setDeleteConfirm(false);
  }, []);

  const accountTypeEntry: ContactDetailsSectionDisplayableEntry = {
    key: 'accountType',
    displayValue: tr(FiatAccount.getAccountType(paymentMethod)),
  };

  const getBankAccountEntries =
    React.useCallback((): readonly ContactDetailsSectionDisplayableEntry[] => {
      const { accountDetails } = paymentMethod;

      return match<AccountDetails, readonly ContactDetailsSectionDisplayableEntry[]>(accountDetails)
        .when(
          isWireAccountDetails,
          (
            wireAccountDetails: WireAccountDetails,
          ): readonly ContactDetailsSectionDisplayableEntry[] => {
            const { wireDetails } = wireAccountDetails;
            return [
              {
                key: 'bankName',
                displayValue: `${wireDetails.bankInfo.name} (USD)`,
              },
              {
                key: 'routingNumber',
                label: tr('ROUTING_NUMBER_DETAILS'),
                displayValue: wireDetails.personInfo.routingKey,
              },
              {
                key: 'accountNumber',
                label: tr('ACCOUNT_NUMBER_DETAILS'),
                displayValue: wireDetails.personInfo.accountNumber,
              },
            ];
          },
        )
        .when(
          isACHAccountDetails,
          (
            achAccountDetails: ACHAccountDetails,
          ): readonly ContactDetailsSectionDisplayableEntry[] => {
            const { achDetails } = achAccountDetails;
            return [
              {
                key: 'bankName',
                displayValue: `${achDetails.bankInfo.name} (USD)`,
              },
              {
                key: 'routingNumber',
                label: tr('ROUTING_NUMBER_DETAILS'),
                displayValue: achDetails.personInfo.routingKey,
              },
              {
                key: 'accountNumber',
                label: tr('ACCOUNT_NUMBER_DETAILS'),
                displayValue: achDetails.personInfo.accountNumber,
              },
            ];
          },
        )
        .when(
          isSwiftAccountDetails,
          (
            swiftAccountDetails: SwiftAccountDetails,
          ): readonly ContactDetailsSectionDisplayableEntry[] => {
            const { swiftDetails } = swiftAccountDetails;
            return [
              {
                key: 'bankName',
                displayValue: `${swiftDetails.bankInfo.name} (USD)`,
              },
              {
                key: 'swiftCode',
                label: tr('SWIFT_CODE'),
                displayValue: swiftDetails.bankInfo.swiftCode,
              },
              {
                key: 'iban',
                label:
                  swiftDetails.personInfo?.iban === undefined ? tr('ACCOUNT_NUMBER') : tr('IBAN'),
                // Note: here `personInfo` is not null, so ?? '' is
                //       never evaluated
                displayValue:
                  swiftDetails.personInfo?.iban ?? swiftDetails.businessInfo?.accountNumber ?? '',
              },
            ];
          },
        )
        .when(
          isPIXAccountDetails,
          (
            pixAccountDetails: PIXAccountDetails,
          ): readonly ContactDetailsSectionDisplayableEntry[] => {
            const { pixDetails } = pixAccountDetails;

            return [
              {
                key: 'pixKey',
                label: tr('PIX_KEY'),
                displayValue: pixDetails.pixKey,
              },
            ];
          },
        )
        .when(
          isCOPAccountDetails,
          (
            copAccountDetails: COPAccountDetails,
          ): readonly ContactDetailsSectionDisplayableEntry[] => {
            const { copDetails } = copAccountDetails;
            return [
              {
                key: 'bankCode',
                label: tr('BANK_CODE'),
                displayValue: copDetails.bankCode,
              },
              {
                key: 'accountType',
                label: tr('ACCOUNT_TYPE'),
                displayValue: copDetails.accountType === 1 ? tr('CHECKING') : tr('SAVINGS'),
              },
              {
                key: 'bankAccount',
                label: tr('BANK_ACCOUNT'),
                displayValue: copDetails.bankAccount,
              },
            ];
          },
        )
        .when(
          isSpeiAccountDetails,
          (
            speiAccountDetails: SpeiAccountDetails,
          ): readonly ContactDetailsSectionDisplayableEntry[] => {
            const { speiDetails } = speiAccountDetails;
            return [
              {
                key: 'clabe',
                label: tr('CLABE'),
                displayValue: speiDetails.clabe,
              },
            ];
          },
        )
        .when(
          isCVUAccountDetails,
          (
            cvuAccountDetails: CVUAccountDetails,
          ): readonly ContactDetailsSectionDisplayableEntry[] => {
            const { cvuDetails } = cvuAccountDetails;
            return [
              {
                key: 'cvu',
                displayValue: cvuDetails.cvu,
              },
            ];
          },
        )
        .exhaustive();
    }, [paymentMethod, tr]);

  return (
    <Paper elevation={3}>
      <Box borderRadius={2} px={3} py={2}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mt="2px" mr="-4px">
          <Typography variant="body2" color="text.primary" fontWeight={500}>
            {paymentMethod.name}
          </Typography>
          {!paymentMethod.belongsToBeneficiary && (
            <IconButton sx={styles.iconButton} size="small" onClick={onDeleteConfirm}>
              <TrashIcon fill="currentColor" />
            </IconButton>
          )}
        </Box>
        <Box mt="18px" display="flex" flexDirection="column" gap={2}>
          <ContactDetailsSection title={tr('ACCOUNT_TYPE')} values={[accountTypeEntry]} />
          <ContactDetailsSection
            title={tr('BANK_ACCOUNT_INFORMATION')}
            values={getBankAccountEntries()}
          />
        </Box>

        <ConfirmationModal
          open={deleteConfirm}
          onAbort={onDeleteAborted}
          onConfirm={onDeleteConfirmed}
          confirmButtonText={tr('DELETE_ACCOUNT')}
          abortButtonText={tr('KEEP_ACCOUNT')}
          title={tr('ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_ACCOUNT_TITLE')}
          description={tr('THIS_ACTION_IS_NOT_REVERSIBLE')}
          loading={deleteFiatAccountMutation.status === QueryStatus.pending}
        />
      </Box>
    </Paper>
  );
};

const styles: Record<string, SystemStyleObject> = {
  iconButton: {
    color: 'text.primary',
  },
};
